import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { GENDER_BG_COLORS } from "../../pages/AdminDashboard";

const buildManagedCars = (trip) => {
  if (!trip?.cars) return [];
  return trip.cars.map((carEntry) => {
    const passengers = (carEntry.passengers || []).filter(Boolean);
    const passengerMap = new Map(
      passengers.map((p) => [p._id?.toString(), p]).filter(([id]) => !!id)
    );
    const currentOrder = (carEntry.passengerDisplayOrder || [])
      .map((id) => id?.toString())
      .filter((id) => id && passengerMap.has(id));
    const missingIds = [...passengerMap.keys()].filter((id) => !currentOrder.includes(id));
    const ids = [...currentOrder, ...missingIds];
    const stopColors = carEntry.passengerStopColors || {};
    return {
      carId: carEntry.carId?._id || carEntry.carId,
      label: `${carEntry.carId?.name || "Vehicle"} ${carEntry.carId?.number || ""}`.trim(),
      passengers: ids.map((id) => {
        const p = passengerMap.get(id);
        return {
          _id: id,
          name: p?.name || "Deleted Passenger",
          stop: p?.stop?.name || p?.stop || "-",
          gender: p?.gender || "",
          stopColor: stopColors[id] || ""
        };
      })
    };
  });
};

const moveSelectedByDelta = (list, selectedSet, delta) => {
  if (selectedSet.size === 0) return list;
  const indexes = list.map((item, idx) => (selectedSet.has(item._id) ? idx : -1)).filter((idx) => idx >= 0);
  if (indexes.length === 0) return list;
  if (delta < 0 && indexes[0] === 0) return list;
  if (delta > 0 && indexes[indexes.length - 1] === list.length - 1) return list;
  const next = [...list];
  if (delta < 0) {
    indexes.forEach((idx) => {
      const tmp = next[idx - 1];
      next[idx - 1] = next[idx];
      next[idx] = tmp;
    });
  } else {
    [...indexes].reverse().forEach((idx) => {
      const tmp = next[idx + 1];
      next[idx + 1] = next[idx];
      next[idx] = tmp;
    });
  }
  return next;
};

export default function PassengerListManager({ open, trip, onClose, onSaved }) {
  const [cars, setCars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedByCar, setSelectedByCar] = useState({});
  const [bulkColorByCar, setBulkColorByCar] = useState({});
  const [dragState, setDragState] = useState(null);

  useEffect(() => {
    if (open && trip) {
      setCars(buildManagedCars(trip));
      setSelectedByCar({});
      setBulkColorByCar({});
      setDragState(null);
    }
  }, [open, trip]);

  const totalPassengers = useMemo(
    () => cars.reduce((sum, car) => sum + car.passengers.length, 0),
    [cars]
  );

  const togglePassenger = (carId, passengerId) => {
    setSelectedByCar((prev) => {
      const current = new Set(prev[carId] || []);
      if (current.has(passengerId)) current.delete(passengerId);
      else current.add(passengerId);
      return { ...prev, [carId]: [...current] };
    });
  };

  const toggleAllInCar = (carId, passengerIds) => {
    setSelectedByCar((prev) => {
      const current = new Set(prev[carId] || []);
      const allSelected = passengerIds.length > 0 && passengerIds.every((id) => current.has(id));
      return { ...prev, [carId]: allSelected ? [] : [...passengerIds] };
    });
  };

  const bulkMove = (carId, delta) => {
    setCars((prev) =>
      prev.map((car) => {
        if (car.carId !== carId) return car;
        const selectedSet = new Set(selectedByCar[carId] || []);
        return { ...car, passengers: moveSelectedByDelta(car.passengers, selectedSet, delta) };
      })
    );
  };

  const bulkSetColor = (carId, color) => {
    const selectedSet = new Set(selectedByCar[carId] || []);
    if (selectedSet.size === 0) return;
    setCars((prev) =>
      prev.map((car) => {
        if (car.carId !== carId) return car;
        return {
          ...car,
          passengers: car.passengers.map((p) =>
            selectedSet.has(p._id) ? { ...p, stopColor: color } : p
          )
        };
      })
    );
  };

  const updateStopColor = (carId, passengerId, color) => {
    setCars((prev) =>
      prev.map((car) => {
        if (car.carId !== carId) return car;
        return {
          ...car,
          passengers: car.passengers.map((p) =>
            p._id === passengerId ? { ...p, stopColor: color } : p
          )
        };
      })
    );
  };

  const onDragStart = (carId, passengerId) => {
    setDragState({ carId, passengerId });
  };

  const onDrop = (carId, targetPassengerId) => {
    if (!dragState || dragState.carId !== carId || dragState.passengerId === targetPassengerId) return;
    setCars((prev) =>
      prev.map((car) => {
        if (car.carId !== carId) return car;
        const fromIndex = car.passengers.findIndex((p) => p._id === dragState.passengerId);
        const toIndex = car.passengers.findIndex((p) => p._id === targetPassengerId);
        if (fromIndex < 0 || toIndex < 0) return car;
        const nextPassengers = [...car.passengers];
        const [item] = nextPassengers.splice(fromIndex, 1);
        nextPassengers.splice(toIndex, 0, item);
        return { ...car, passengers: nextPassengers };
      })
    );
    setDragState(null);
  };

  const saveChanges = async () => {
    if (!trip?._id) return;
    setSaving(true);
    try {
      const payload = cars.map((car) => ({
        carId: car.carId,
        passengerOrder: car.passengers.map((p) => p._id),
        stopColors: car.passengers.reduce((acc, p) => {
          if (p.stopColor) acc[p._id] = p.stopColor;
          return acc;
        }, {})
      }));
      await api(`/admin/trips/${trip._id}/manage-list`, {
        method: "POST",
        body: { cars: payload }
      });
      await onSaved();
      onClose();
    } catch (err) {
      alert(err.message || "Failed to save list settings");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !trip) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.45)",
        zIndex: 1000,
        padding: "1rem"
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "#ffffff",
          borderRadius: "12px",
          overflow: "auto",
          padding: "1rem"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            marginBottom: "1rem",
            alignItems: "center"
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Manage Coordinator List</h3>
            <p style={{ margin: "0.3rem 0 0 0", color: "#6b7280", fontSize: "0.875rem" }}>
              Reorder passengers within each vehicle and set stop-cell colors. Total passengers: {totalPassengers}
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button className="btn secondary" onClick={onClose} disabled={saving}>
              Close
            </button>
            <button className="btn success" onClick={saveChanges} disabled={saving}>
              {saving ? "Saving..." : "Save List"}
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
          {cars.map((car) => {
            const passengerIds = car.passengers.map((p) => p._id);
            const selectedSet = new Set(selectedByCar[car.carId] || []);
            const selectedCount = selectedSet.size;
            const allSelected = passengerIds.length > 0 && passengerIds.every((id) => selectedSet.has(id));
            return (
              <div
                key={car.carId}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  padding: "0.75rem",
                  background: "#f9fafb"
                }}
              >
                <h4 style={{ margin: "0 0 0.5rem 0" }}>{car.label}</h4>
                {selectedCount > 0 && (
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
                    <button
                      className="btn"
                      style={{ fontSize: "0.75rem", padding: "0.3rem 0.55rem" }}
                      onClick={() => bulkMove(car.carId, -1)}
                    >
                      Move Selected Up
                    </button>
                    <button
                      className="btn"
                      style={{ fontSize: "0.75rem", padding: "0.3rem 0.55rem" }}
                      onClick={() => bulkMove(car.carId, 1)}
                    >
                      Move Selected Down
                    </button>
                    <input
                      type="color"
                      value={bulkColorByCar[car.carId] || "#ffffff"}
                      onChange={(e) => setBulkColorByCar((prev) => ({ ...prev, [car.carId]: e.target.value }))}
                    />
                    <button
                      className="btn"
                      style={{ fontSize: "0.75rem", padding: "0.3rem 0.55rem" }}
                      onClick={() => bulkSetColor(car.carId, bulkColorByCar[car.carId] || "#ffffff")}
                    >
                      Apply Color
                    </button>
                    <button
                      className="btn secondary"
                      style={{ fontSize: "0.75rem", padding: "0.3rem 0.55rem" }}
                      onClick={() => bulkSetColor(car.carId, "")}
                    >
                      Clear Color
                    </button>
                  </div>
                )}
                <div className="scroll-x">
                  <table className="table table-list" style={{ background: "#fff" }}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleAllInCar(car.carId, passengerIds)}
                          />
                        </th>
                        <th>Sr#</th>
                        <th>Name</th>
                        <th>Stop</th>
                        <th>Order</th>
                        <th>Color</th>
                      </tr>
                    </thead>
                    <tbody>
                      {car.passengers.length === 0 && (
                        <tr>
                          <td colSpan={6}>No passengers</td>
                        </tr>
                      )}
                      {car.passengers.map((p, index) => (
                        <tr
                          key={p._id}
                          draggable
                          onDragStart={() => onDragStart(car.carId, p._id)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => onDrop(car.carId, p._id)}
                          style={{ cursor: "grab" }}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedSet.has(p._id)}
                              onChange={() => togglePassenger(car.carId, p._id)}
                            />
                          </td>
                          <td>{index + 1}</td>
                          <td style={{ backgroundColor: GENDER_BG_COLORS[p.gender] || "#ffffff" }}>{p.name}</td>
                          <td style={{ backgroundColor: p.stopColor || "transparent" }}>{p.stop}</td>
                          <td>
                            <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Drag row</span>
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                              <input
                                type="color"
                                value={p.stopColor || "#ffffff"}
                                onChange={(e) => updateStopColor(car.carId, p._id, e.target.value)}
                              />
                              <button
                                className="btn secondary"
                                style={{ fontSize: "0.7rem", padding: "0.2rem 0.45rem" }}
                                onClick={() => updateStopColor(car.carId, p._id, "")}
                              >
                                Clear
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
